import { TinyColor } from '@ctrl/tinycolor';
import { App, Button, Flex, Modal, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CalendarDays, CircleAlert, CircleCheck, Copy, PlusCircle, Trash2 } from 'lucide-react';
import {
  createContext,
  type Key,
  type ReactNode,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { catchError, EMPTY, tap } from 'rxjs';
import { CycleMenuSitesGroupsComponent } from './cycle-menu-sites-groups.component';
import CycleMenuForm from './cycle-menu.form';
import CycleMenusCalendarModal from './cycle-menus-calendar.modal';
import CycleMenusFilter, { type CycleMenusFiltersProps } from './cycle-menus.filter';
import ScheduledMealFilter, {
  type ScheduledMealFiltersProps,
} from './scheduled-meals/scheduled-meal.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { type ScheduledMealResponseDto } from '../../../dto/scheduled-meal/scheduled-meal-response-dto';
import { CycleMenu, type Site, type Customer } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import useScheduledMealsList, {
  type ScheduledMealsListParams,
} from '../../../hooks/use-scheduled-meals-list';
import { AtomicOperation, useQuery } from '../../../jsonapi-provider';
import useAtomicOperations from '../../../jsonapi-provider/hooks/use-atomic-operations';
import { type DataContext, Roles } from '../../../types';
import { LocalDate } from '../../../utils/local-date';
import utils from '../../../utils/utils';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Cycle Menus deleted successfully';

interface CycleMenusPageState {
  filters?: CycleMenusFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

export interface ScheduledMealPageState {
  filters?: ScheduledMealFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const reducer = (
  state: CycleMenusPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?:
      | CycleMenusPageState['filters']
      | CycleMenusPageState['pagination']
      | CycleMenusPageState['sort'];
  }
): CycleMenusPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as CycleMenusPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as CycleMenusPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as CycleMenusPageState['sort'],
      };
  }
};

export const scheduledMealReducer = (
  state: ScheduledMealPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?:
      | ScheduledMealPageState['filters']
      | ScheduledMealPageState['pagination']
      | ScheduledMealPageState['sort'];
  }
): ScheduledMealPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as ScheduledMealPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as ScheduledMealPageState['sort'],
      };
  }
};

const serializeFilters = (filters: CycleMenusPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  if (filters?.startDate) {
    const [start, end] = filters.startDate.map((date) => date.toISOString());
    parts.push(`startDate=ge=${start}`, `startDate=le=${end}`);
  }

  if (filters?.endDate) {
    const [start, end] = filters.endDate.map((date) => date.toISOString());
    parts.push(`endDate=ge=${start}`, `endDate=le=${end}`);
  }

  return parts.length ? parts.join(';') : undefined;
};

const serializeSort = (sort: CycleMenusPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};
export const buildParentEntityToSite = (sites: Site[]): Record<string, Record<string, Site[]>> => {
  const siteElementParent: Record<string, Record<string, Site[]>> = {
    agencies: {},
    counties: {},
    customers: {},
  };
  Object.values(
    utils.groupBy(
      sites.filter((site) => site.agency && site.state && site.county && site.customer),
      ({ agency }) => agency.id!
    )
  ).forEach((sitesByAgency) => {
    siteElementParent.agencies[`A@${sitesByAgency[0].agency.id}`] = sitesByAgency;
    Object.values(
      utils.groupBy(sitesByAgency, ({ state, county }) => `${state}/${county}`)
    ).forEach((sitesByCounty) => {
      siteElementParent.counties[
        `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`
      ] = sitesByCounty;
      Object.values(utils.groupBy(sitesByCounty, ({ customer }) => customer.id!)).forEach(
        (sitesByCustomer) => {
          siteElementParent.customers[
            `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`
          ] = sitesByCustomer;
        }
      );
    });
  });
  return siteElementParent;
};

export const CycleMenusContext = createContext<DataContext<CycleMenu[]>>({});

const CYCLE_MENUS_PAGE_SIZE = 10000;

const CycleMenusPage = (): ReactNode => {
  const { modal, notification } = App.useApp();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [cycleMenuCalendarOpen, setCycleMenuCalendarOpen] = useState(false);
  const [cloningInProgress, setCloningInProgress] = useState(false);
  const [clonedRecord, setClonedRecord] = useState<CycleMenu | undefined>(undefined);
  const [parentEntityToSites, setParentEntityToSites] = useState<
    Record<string, Record<string, Site[]>>
  >({});
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: CYCLE_MENUS_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [scheduledMealState, setScheduledMealState] = useReducer(scheduledMealReducer, {
    filters: { orderDate: new LocalDate() },
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [scheduledMealDebouncedState] = useDebouncedState({
    state: scheduledMealState,
    time: 1000,
  });

  const scheduledMealsParams: ScheduledMealsListParams = useMemo(
    () => ({
      day: scheduledMealDebouncedState.filters?.orderDate
        ? new LocalDate(scheduledMealDebouncedState.filters.orderDate).toISOString()
        : undefined,
      siteId: scheduledMealDebouncedState.filters?.site
        ? Number(scheduledMealDebouncedState.filters.site)
        : undefined,
      pageNumber: scheduledMealDebouncedState.pagination.current,
      pageSize: scheduledMealDebouncedState.pagination.pageSize,
    }),
    [
      scheduledMealDebouncedState.filters?.orderDate,
      scheduledMealDebouncedState.filters?.site,
      scheduledMealDebouncedState.pagination.current,
      scheduledMealDebouncedState.pagination.pageSize,
    ]
  );
  const scheduledMeals = useScheduledMealsList(scheduledMealsParams);
  const [cycleMenuToEdit, setCycleMenuToEdit] = useState<CycleMenu>();

  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      sort: 'name',
      fields: {
        customer: ['name', 'sites'],
        site: [
          'name',
          'state',
          'county',
          'agency',
          'siteNumber',
          'custSiteNumber',
          'displaySiteNumber',
        ],
        agency: ['name'],
      },
      include: ['sites.agency'],
      page: { size: 10000 },
    },
  ]);
  const cycleMenus = useQuery<CycleMenu[]>(
    debouncedState && [
      'cycle-menu',
      {
        fields: {
          'cycle-menu': ['name', 'sites', 'startDate', 'endDate', 'repeatUntil', 'isEnabled'],
          agency: ['name'],
          site: [
            'name',
            'customer',
            'agency',
            'state',
            'county',
            'siteNumber',
            'custSiteNumber',
            'displaySiteNumber',
          ],
          customer: ['name'],
        },
        filter: serializeFilters(debouncedState.filters),
        include: ['sites.agency', 'sites.customer'],
        sort: serializeSort(debouncedState.sort),
        page: {
          totals: 1,
          number: debouncedState.pagination.current,
          size: debouncedState.pagination.pageSize,
        },
      },
    ]
  );
  const atomicOperations = useAtomicOperations();

  useEffect(() => {
    setParentEntityToSites(
      buildParentEntityToSite(
        customers.data?.flatMap(
          (customer) => customer?.sites.map((site) => ({ ...site, customer }))
        ) ?? []
      )
    );
  }, [customers.data]);
  useEffect(() => {
    customers.error &&
      notification.error({
        message: ' ',
        description: customers.error.detail,
      });
  }, [customers.error]);

  useEffect(() => {
    scheduledMeals.errorMessage &&
      notification.error({
        message: ' ',
        description: scheduledMeals.errorMessage,
      });
  }, [scheduledMeals.errorMessage]);

  useEffect(() => {
    cycleMenus.error &&
      notification.error({
        message: ' ',
        description: cycleMenus.error.detail,
      });
  }, [cycleMenus.error]);

  const cycleMenusColumns: DataTableProps<CycleMenu>['columns'] = [
    {
      title: 'Name',
      width: 200,
      dataIndex: 'name',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setCycleMenuToEdit(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      dataIndex: 'startDate',
      title: 'Time Frame',
      render: (_value: CycleMenu['startDate'], record: CycleMenu) => (
        <>
          {`${record.startDate.format()} - ${record.endDate.format()}`}{' '}
          <Link to={record.id ?? ''}>
            <Typography.Text
              italic
              underline
              style={{ marginLeft: 10 }}
              // onClick={() => {
              //   // setIsModalOpen(true);
              //   setSelectedCycleMenu(record);
              // }}
            >
              Schedule meals
            </Typography.Text>
          </Link>
        </>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      dataIndex: 'repeatUntil',
      title: 'Repeat until',
      width: 170,
      render: (value: CycleMenu['repeatUntil']) => value?.format() ?? '-',
    },
    {
      dataIndex: 'sites',
      title: (
        <Flex vertical>
          <Typography.Text disabled>Sites</Typography.Text>
          <Flex gap="middle" wrap="wrap" align="center">
            <Flex align="center">
              <div className="circle" style={{ background: token.colorPrimary }} />
              <small>Agency</small>
            </Flex>
            <Flex align="center">
              <div
                className="circle"
                style={{
                  background: new TinyColor(token.colorPrimary).setAlpha(0.3).toHslString(),
                }}
              />
              <small>County</small>
            </Flex>
            <Flex align="center">
              <div
                className="circle"
                style={{
                  background: new TinyColor(token.colorPrimary).setAlpha(0.1).toHslString(),
                }}
              />
              <small>Customer</small>
            </Flex>
            <Flex align="center">
              <div
                className="circle"
                style={{
                  border: `1px solid var(--ant-color-primary)`,
                }}
              />
              <small>Site</small>
            </Flex>
          </Flex>
        </Flex>
      ),
      render: (_sites: Site[], record: CycleMenu) => {
        return (
          <CycleMenuSitesGroupsComponent
            cycleMenu={record}
            parentEntityToSites={parentEntityToSites}
          />
        );
      },
    },
    {
      dataIndex: 'isEnabled',
      title: 'Enabled',
      render: (value: CycleMenu['isEnabled']) =>
        value ? (
          <Flex align="center" gap="small" style={{ minWidth: 80 }}>
            <CircleCheck style={{ color: token.colorTextDisabled }} />
            <Typography.Text>Enabled</Typography.Text>
          </Flex>
        ) : (
          <span></span>
        ),
    },
  ];

  const scheduledMealColumns: DataTableProps<ScheduledMealResponseDto>['columns'] = [
    {
      dataIndex: ['mealCategory', 'name'],
      title: 'Meal Category',
      onCell: () => {
        return {
          style: { padding: token.paddingXS },
        };
      },
    },
    {
      dataIndex: ['meal', 'name'],
      title: 'Meal',
      onCell: () => {
        return {
          style: { padding: token.paddingXS },
        };
      },
      render: (_, record) => {
        return (
          <Typography.Text style={{ justifySelf: 'flex-start' }}>
            {record.meal?.name ?? '-'}
          </Typography.Text>
        );
      },
    },
    {
      dataIndex: ['extraMealItem1', 'name'],
      title: 'Extra Meal Item 1',
      onCell: () => {
        return {
          style: { padding: token.paddingXS },
        };
      },
      render: (value, record) => {
        return record.extraMealItem1 ? (
          <Link to={`/meal-items?id=${record.extraMealItem1?.id ?? '#'}`}>
            <Typography.Text style={{ justifySelf: 'flex-start' }}>
              {record.extraMealItem1?.name}
            </Typography.Text>
          </Link>
        ) : undefined;
      },
    },
    {
      dataIndex: ['extraMealItem2', 'name'],
      title: 'Extra Meal Item 2',
      onCell: () => {
        return {
          style: { padding: token.paddingXS },
        };
      },
      render: (value, record) => {
        return record.extraMealItem1 ? (
          <Link to={`/meal-items?id=${record.extraMealItem2?.id ?? '#'}`}>
            <Typography.Text style={{ justifySelf: 'flex-start' }}>
              {record.extraMealItem2?.name}
            </Typography.Text>
          </Link>
        ) : undefined;
      },
    },
    {
      dataIndex: ['cycleMenu', 'name'],
      title: 'Cycle Menu',
      onCell: () => {
        return {
          style: { padding: token.paddingXS },
        };
      },
    },
    {
      dataIndex: ['price'],
      title: 'Price',
      render: (value: number) => utils.currencyFormat(value),
      onCell: () => {
        return {
          style: { padding: token.paddingXS },
        };
      },
    },
  ];

  return (
    <Flex gap="middle" vertical>
      <DataTable<ScheduledMealResponseDto>
        title="Scheduled meals"
        description="List of actual scheduled meals to be ordered"
        filtersNode={
          <ScheduledMealFilter
            initialFilters={scheduledMealState.filters}
            showDate={true}
            onChange={(filters) => {
              setScheduledMealState({ type: 'SET_FILTER', payload: filters });
            }}
            customers={customers}
          />
        }
        currentPage={scheduledMealState.pagination.current}
        pageSize={scheduledMealState.pagination.pageSize}
        totalRecords={scheduledMeals.totalRecords}
        dataList={
          scheduledMeals.data?.toSorted(
            (a, b) => (a.mealCategory?.sortOrder ?? 0) - (b.mealCategory?.sortOrder ?? 0)
          ) ?? []
        }
        onChange={setScheduledMealState}
        columns={scheduledMealColumns}
        loading={scheduledMeals.isLoading || customers.isLoading}
      />
      <DataTable<CycleMenu>
        title="Cycle menus"
        description="List of scheduled recurring menus to be ordered"
        filtersNode={
          <CycleMenusFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Flex gap="middle">
              <Button
                icon={<CalendarDays />}
                onClick={() => {
                  setCycleMenuCalendarOpen(true);
                }}
              >
                Calendar View
              </Button>
              <Button
                type="primary"
                icon={<PlusCircle />}
                onClick={() => {
                  setCycleMenuToEdit(new CycleMenu());
                }}
              >
                Add Cycle Menu
              </Button>
            </Flex>
          </AuthorizedComponent>
        }
        dataList={cycleMenus}
        onChange={setState}
        columns={cycleMenusColumns}
        pageSize={CYCLE_MENUS_PAGE_SIZE}
        actions={[
          {
            key: 'clone',
            label: 'Clone',
            icon: <Copy />,
            onItemClick: (record) => {
              setClonedRecord(record);
            },
          },
          { type: 'divider' },
          {
            key: 'delete',
            label: 'Delete',
            danger: true,
            icon: <Trash2 />,
            onItemClick: (record) => {
              void modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: async () => {
                  atomicOperations
                    .operate([
                      AtomicOperation.remove(undefined, {
                        id: record.id ?? '',
                        type: 'cycle-menu',
                      }),
                    ])
                    .pipe(
                      tap(() => {
                        notification.success({
                          message: 'Cycle menu deletion succeeded',
                          description: SUCCESS_DELETE_MESSAGE,
                        });
                        cycleMenus.refetch?.();
                      }),
                      catchError((error) => {
                        notification.error({
                          description: error.message ?? 'Server is unreachable',
                          message: '',
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={cycleMenuToEdit}>
        <ExtraContent>
          <CycleMenuForm
            cycleMenu={cycleMenuToEdit!}
            onSuccess={(cm, isProgressive) => {
              setCycleMenuToEdit(undefined);
              // fetchCycleMenus();
              isProgressive && navigate(cm.id!);
              !isProgressive && cycleMenus.refetch?.();
            }}
            onCancel={() => {
              setCycleMenuToEdit(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
      <CycleMenusCalendarModal
        isOpen={cycleMenuCalendarOpen}
        customers={
          customers.data ? (Array.isArray(customers.data) ? customers.data : [customers.data]) : []
        }
        onConfirm={() => {
          setCycleMenuCalendarOpen(false);
        }}
        onCancel={() => {
          setCycleMenuCalendarOpen(false);
        }}
      />
      <Modal
        title={
          <Typography.Text>
            Confirm cloning <b>{clonedRecord?.name}</b>
          </Typography.Text>
        }
        okText="Clone"
        okType="primary"
        okButtonProps={{ htmlType: 'submit', form: 'cycle-menu-form' }}
        cancelText="Discard"
        open={clonedRecord !== undefined}
        confirmLoading={cloningInProgress}
        onCancel={() => {
          setClonedRecord(undefined);
        }}
        destroyOnHidden={true}
      >
        <CycleMenuForm
          isClone
          cycleMenuToBeClonedId={clonedRecord?.id}
          cycleMenu={Object.assign({}, clonedRecord, {
            name: `${clonedRecord?.name} - clone`,
            id: undefined,
            sites: [],
            agencies: [],
          })}
          setCloningInProgress={setCloningInProgress}
          cloningInProgress={cloningInProgress}
          onSuccess={() => {
            cycleMenus.refetch?.();
            setClonedRecord(undefined);
            setCloningInProgress(false);
          }}
          onCancel={() => {
            setClonedRecord(undefined);
            setCloningInProgress(false);
          }}
        />
      </Modal>
    </Flex>
  );
};

export default CycleMenusPage;
